<script>
  import { createEventDispatcher } from 'svelte';
  import { expoOut } from 'svelte/easing';
  const easing = expoOut;

  import { getRelationshipColor, getRelationshipIcon } from './statuses.js';
  const dispatch = createEventDispatcher();
  
  export let relationship = null;
  export let selectedCharacter = null;
  export let selectedRelationship = null;
  export let selectedDimension = null;

  const animation_duration = 1000;

  const getRelationshipDescription = (rel) => (rel.status != null) ? rel.status : '?';

  const dim = (current_rel, selected_char, selected_rel, selected_dim) => {
    // Relationship dimming.
    if (selected_rel && Object.keys(selected_rel).length) {
      const has_shared_chars = (
        current_rel.characters[0] == selected_rel.characters[0] 
        || current_rel.characters[1] == selected_rel.characters[1]);
      if (selected_rel.characters == current_rel.characters) {
        return 'selected';
      }
      else if (has_shared_chars) {
        return 'shared-character';
      }
      return 'dimmed';
    }

    // Check for character-based dimming.
    if (selected_char && Object.keys(selected_char).length) {
      if (current_rel.characters[selected_dim] == selected_char) {
        return 'selected';
      }
      return 'dimmed';
    }
  }

  const handleClick = () => {
    dispatch('relationshipPopup', relationship);
  };

  const highlight_col = (node, params) => {
    const dy = node.getBoundingClientRect().top - node.closest('tbody').getBoundingClientRect().top;
    const height = node.parentNode.offsetHeight + 12;

    return {
      delay: 0,
      duration: animation_duration,
      easing: easing,
      tick: (t, u) => {
        if (t < 0.5) {
          node.style.height = (t * 2 * dy) + "px";
          node.style.top = (-1 * t * 2 * dy) - 5 + height + 'px';
        }
        else {
          node.style.height = (u * 2 * dy) + "px";
          node.style.top = (-1 * u * 2 * dy) - 5 + height + 'px';
        }
      }
    };
  }

  const highlight_row = (node, params) => {
    const dx = node.getBoundingClientRect().left - node.closest('tr').getBoundingClientRect().left - node.closest('tr').firstElementChild.getBoundingClientRect().width;

    return {
      delay: 0,
      duration: animation_duration,
      easing: easing,
      tick: (t, u) => {
        if (t < 0.5) {
          node.style.width = t * 2 * dx + "px";
          node.style.left = (-1 * t * 2 * dx) - 9 + 'px';
        }
        else {
          node.style.width = u * 2 * dx + "px";
          node.style.left = (-1 * u * 2 * dx) - 9 + 'px';
        }
      }
    };
  }
</script>

<style global>
  .cell.pro {
    background-color: rgba(127,190,112,.15);
    border-left: 4px solid #7FBE70;
    color: #7FBE70;
  }

  .cell.complicated-pro {
    background-color: rgba(127,190,112,.15);
    border-left: 4px solid #7FBE70;
    color: #7FBE70;
  }

  .cell.complicated {
    background-color: rgba(243,169,32,.15);
    border-left: 4px solid #F3A920;
    color: #F3A920;
  }

  .cell.complicated-con {
    background-color: rgba(209,43,84,.2);
    border-left: 4px solid #D12B54;
    color: #D12B54;
  }

  .cell.con {
    background-color: rgba(209,43,84,.2);
    border-left: 4px solid #D12B54;
    color: #D12B54;
  }

  .cell.na {
    background-color: #F2F2F2;
    color: #D1D3D4;
  }

  .cell.unknown {
    background-color: #EEEEEE;
  }

  .cell.empty {
    background-color: transparent;
  }

  .cell {
    transition: ease-in-out .3s;
    box-sizing: border-box;
    display: block;
    border-radius: 4px;
    margin: 0.33em;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    background-color: white;
  }
  .cell.selected .animation {
    position: absolute;
    z-index: -1;
    background-color: lightgray;
  }
  .cell.selected .animation.pro,
  .cell.selected .animation.complicated-pro {
    background-color: rgba(127,190,112,.15);
  }
  .cell.selected .animation.con,
  .cell.selected .animation.complicated-con {
    background-color: rgba(209,43,84,.2);
  }
  .cell.selected .animation.complicated {
    background-color: rgba(243,169,32,.15);
  }
  .cell.selected .column-animation {
    bottom: 0;
    top: 0;
    height: 0;
    left: -10px; right: -6px;
    /* border-left: 3px solid gray;
    border-right: 3px solid gray; */
    mask-image: linear-gradient(to top, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
  }
  .cell.selected .row-animation {
    top: -7px;
    bottom: -7px;
    right: 0;
    width: 0;
    /* border-top: 3px solid gray;
    border-bottom: 3px solid gray; */
    mask-image: linear-gradient(to left, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0));
  }
  .cell svg {
    width: auto;
    height: 11px;
  }
  .dimmed {
    filter: grayscale(100%);
    opacity: 0.2;
  }
  .shared-character {
    opacity: 0.5;
  }

  @media (min-width: 1260px) {
    .cell {
      margin: .3em;
      height: 45px;
      width: 45px;
    }
    .cell svg {
      height: 15px;
    }
  }
</style>

{#if relationship == null}
<div class="cell empty">
  <span class="visually-hidden">Identity relationship</span>
</div>
{:else}
<button class="cell {getRelationshipColor(relationship.status)} {dim(relationship, selectedCharacter, selectedRelationship, selectedDimension)}" on:click={handleClick} title="{getRelationshipDescription(relationship)}">
  {#if dim(relationship, null, selectedRelationship, selectedDimension) == 'selected' }
    <div class="animation row-animation {getRelationshipColor(relationship.status)}" in:highlight_row></div>
    <div class="animation column-animation {getRelationshipColor(relationship.status)}" in:highlight_col></div>
  {/if}
  <span class="visually-hidden">{getRelationshipDescription(relationship)}</span>
  {#if getRelationshipIcon(relationship.status)}
  <svg xmlns="http://www.w3.org/2000/svg">
    <use href="{getRelationshipIcon(relationship.status)}"/>
  </svg>
  {/if}
</button>
{/if}